import { 
  format, 
  isValid, 
  formatDistanceToNow,
  startOfDay,
  parseISO,
  getTime
} from 'date-fns';
import { enUS, es } from 'date-fns/locale';
import useLocale from 'i18n/useLocale';


// ----------------------------------------------------------------------

export type DatePickerFormat = Date | string | number | null | undefined;

/**
 * Docs: https://date-fns.org/docs/format
 */
export const formatStr = {
  dateTime: 'dd MMM yyyy h:mm a', // 17 Apr 2022 12:00 am
  date: 'dd MMM yyyy', // 17 Apr 2022
  time: 'h:mm a', // 12:00 am
  split: {
    dateTime: 'dd/MM/yyyy h:mm a', // 17/04/2022 12:00 am
    date: 'dd/MM/yyyy', // 17/04/2022
  },
  paramCase: {
    dateTime: 'dd-MM-yyyy h:mm a', // 17-04-2022 12:00 am
    date: 'dd-MM-yyyy', // 17-04-2022
  },
};

export function today(formatPattern?: string) {
  return format(startOfDay(new Date()), formatPattern || formatStr.date);
}

// ----------------------------------------------------------------------

function parseDate(date: DatePickerFormat): Date | null {
  if (!date) return null;
  
  try {
    if (date instanceof Date) return date;
    if (typeof date === 'number') return new Date(date);
    return parseISO(date);
  } catch {
    return null;
  }
}

/** output: 17 Apr 2022 12:00 am
 */
export function fDateTime(date: DatePickerFormat, formatPattern?: string) {
  const parsedDate = parseDate(date);
  if (!parsedDate || !isValid(parsedDate)) {
    return 'Invalid time value';
  }
  
  return format(parsedDate, formatPattern || formatStr.dateTime);
}

// ----------------------------------------------------------------------

/** output: 17 Apr 2022
 */
export function fDate(date: DatePickerFormat, formatPattern?: string) {
  const parsedDate = parseDate(date);
  if (!parsedDate || !isValid(parsedDate)) {
    return 'Invalid time value';
  }

  return format(parsedDate, formatPattern || formatStr.date);
}

// ----------------------------------------------------------------------

/** output: 12:00 am
 */
export function fTime(date: DatePickerFormat, formatPattern?: string) {
  const parsedDate = parseDate(date);
  if (!parsedDate || !isValid(parsedDate)) {
    return 'Invalid time value';
  }

  return format(parsedDate, formatPattern || formatStr.time);
}

// ----------------------------------------------------------------------

/** output: 1713250100
 */
export function fTimestamp(date: DatePickerFormat) {
  const parsedDate = parseDate(date);
  if (!parsedDate || !isValid(parsedDate)) {
    return 'Invalid time value';
  }

  return getTime(parsedDate);
}

// ----------------------------------------------------------------------
/** output: a few seconds, 2 years
 */
export function fToNow(date: string | Date) {
  let parsedDate: Date;

  if (typeof date === 'string') {
    parsedDate = parseISO(date);
  } else {
    parsedDate = date;
  }

  if (!parsedDate || !isValid(parsedDate)) {
    return 'Invalid time value';
  }
 
  return formatDistanceToNow(parsedDate, { 
    addSuffix: true, 
    locale: useLocale().locale === 'en' ? enUS : es 
  });}
